import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { editScheduleEffectsActions } from '../actions'
import { changeDateFormat } from '../utils/lessonHelpers'
import { useActions, useAppSelector } from '../../../store/hooks'
import { authSelector } from '../../../store/slices/auth'
import { editSchedulePageSelector } from '../../../store/slices/editSchedulePage'
import { referencesSelector } from '../../../store/slices/references'

export function useEditScheduleEffects() {
    const [searchParams] = useSearchParams()
    const alertClassIds = searchParams.get('classes') || ''

    const isAuthorized = useAppSelector(authSelector.isAuthorized)
    const scheduleFilters = useAppSelector(editSchedulePageSelector.filters)
    const groupOptions = useAppSelector(referencesSelector.groupOptions)
    const teacherOptions = useAppSelector(referencesSelector.teacherOptions)
    const selectedGroupId = useAppSelector(editSchedulePageSelector.selectedGroupId)
    const selectedTeacherId = useAppSelector(editSchedulePageSelector.selectedTeacherId)
    const form = useAppSelector(editSchedulePageSelector.form)

    const {
        referenceLoadEditorData,
        referenceLoadEditorAudience,
        scheduleLoad,
        scheduleLoadForClasses,
        scheduleCheckOccupancy,
        resetOccupancy,
        setSelectedGroupId,
        setSelectedTeacherId,
    } = useActions(editScheduleEffectsActions)

    useEffect(() => {
        if (!isAuthorized) return

        referenceLoadEditorData()
    }, [isAuthorized, referenceLoadEditorData])

    useEffect(() => {
        if (groupOptions.length > 0 && !selectedGroupId) {
            setSelectedGroupId(String(groupOptions[0].value))
        }
    }, [groupOptions, selectedGroupId, setSelectedGroupId])

    useEffect(() => {
        if (teacherOptions.length > 0 && !selectedTeacherId) {
            setSelectedTeacherId(String(teacherOptions[0].value))
        }
    }, [teacherOptions, selectedTeacherId, setSelectedTeacherId])

    useEffect(() => {
        if (!form.building_id) {
            referenceLoadEditorAudience(null)
            return
        }

        referenceLoadEditorAudience(form.building_id)
    }, [form.building_id, referenceLoadEditorAudience])

    useEffect(() => {
        if (
            !form.class_date
            || !form.teacher_id
            || !Array.isArray(form.group_id)
            || form.group_id.length === 0
            || !form.room_id
        ) {
            resetOccupancy()
            return
        }

        scheduleCheckOccupancy({
            day: changeDateFormat(form.class_date) || form.class_date,
            teacherId: form.teacher_id,
            groupIds: form.group_id,
            roomId: form.room_id,
        })
    }, [
        form.teacher_id,
        form.group_id,
        form.class_date,
        form.room_id,
        scheduleCheckOccupancy,
        resetOccupancy,
    ])

    useEffect(() => {
        if (!isAuthorized) return

        if (alertClassIds) {
            scheduleLoadForClasses(alertClassIds)
            return
        }

        scheduleLoad(scheduleFilters)
    }, [isAuthorized, alertClassIds, scheduleFilters, scheduleLoad, scheduleLoadForClasses])
}

export function useAlertClassIds() {
    const [searchParams, setSearchParams] = useSearchParams()
    const alertClassIds = searchParams.get('classes') || ''

    const clearAlertClasses = () => {
        if (!searchParams.has('classes')) return

        const next = new URLSearchParams(searchParams)
        next.delete('classes')
        setSearchParams(next, { replace: true })
    }

    return { alertClassIds, clearAlertClasses }
}
